import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Sparkles, BedDouble, UtensilsCrossed, MapPin, CalendarCheck, ChevronRight, Phone, Globe } from "lucide-react";
import { CHAT_URL, api } from "../lib/api";
import { openBooking } from "../lib/booking";
import { openBookingModal } from "../lib/bookingModal";

const SUGGESTIONS = [
  { icon: BedDouble, label: "Which room suits me?", q: "Which room type do you recommend for my stay?" },
  { icon: UtensilsCrossed, label: "See the menu", q: "What's on your restaurant menu?" },
  { icon: MapPin, label: "Where are you?", q: "Where are you located and how do I get there?" },
];

// Only common languages a country motel is likely to see; English is the fallback.
const LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी (Hindi)" },
  { code: "zh", label: "中文" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "ar", label: "العربية" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "th", label: "ไทย" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
  { code: "ur", label: "اردو" },
];
const RECEPTION_TEL = "0260362077";
const RECEPTION_DISPLAY = "02 6036 2077";

const sid = () => {
  let s = localStorage.getItem("bf_sid");
  if (!s) { s = Math.random().toString(36).slice(2); localStorage.setItem("bf_sid", s); }
  return s;
};

const detectLang = () => {
  const stored = localStorage.getItem("bf_lang");
  if (stored) return stored;
  const codes = LANGS.map((l) => l.code);
  const nav = (navigator.language || "en").toLowerCase().split("-")[0];
  return codes.includes(nav) ? nav : "en";
};

export default function AIConcierge() {
  const [open, setOpen] = useState(false);
  const [teaser, setTeaser] = useState(false);
  const [plan, setPlan] = useState(false);
  const [dates, setDates] = useState({ ci: "", co: "", guests: 2 });
  const [lang, setLang] = useState(detectLang);
  const [langOpen, setLangOpen] = useState(false);
  const [receptionHours, setReceptionHours] = useState("");
  const [msgs, setMsgs] = useState([
    { role: "assistant", text: "Welcome to Byer Fountain Motor Inn — I'm Aria, your personal concierge. I can recommend a room, share our menu, give directions, or help you book direct. How can I help?" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scroller = useRef(null);

  useEffect(() => {
    if (sessionStorage.getItem("bfmi_teaser_dismissed") === "1") return;
    const show = setTimeout(() => setTeaser(true), 3500);
    const hide = setTimeout(() => setTeaser(false), 3500 + 6000); // auto-dismiss after 6s
    return () => { clearTimeout(show); clearTimeout(hide); };
  }, []);
  const dismissTeaser = (e) => { e?.stopPropagation(); setTeaser(false); sessionStorage.setItem("bfmi_teaser_dismissed", "1"); };
  useEffect(() => { scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" }); }, [msgs, open, plan]);
  useEffect(() => {
    api.get("/site-settings").then((r) => {
      const d = r.data || {};
      const s = [d.reception_weekday && `Mon–Fri ${d.reception_weekday}`, d.reception_weekend && `Sat–Sun ${d.reception_weekend}`]
        .filter(Boolean).join(" · ");
      if (s) setReceptionHours(s);
    }).catch(() => {});
  }, []);
  useEffect(() => { localStorage.setItem("bf_lang", lang); }, [lang]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    setBusy(true);
    try {
      const res = await fetch(CHAT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid(), message: q, language: lang }),
      });
      if (!res.ok) throw new Error("chat_http_" + res.status);
      const data = await res.json();
      const full = (data.reply || "").trim() ||
        "I'm not certain about that just now — please call reception on 02 6036 2077 or email admin@byerfountainmotorinn.com.au and our team will gladly help.";
      const showReception = !!data.call_reception;
      let i = 0;
      const step = () => {
        i += Math.max(1, Math.round(full.length / 120));
        const shown = full.slice(0, Math.min(i, full.length));
        setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: shown, callReception: i >= full.length && showReception }; return c; });
        if (i < full.length) setTimeout(step, 18);
      };
      step();
    } catch {
      setMsgs((m) => {
        const c = [...m];
        c[c.length - 1] = {
          role: "assistant",
          text: "I couldn't reach the concierge just now — please call reception on 02 6036 2077 or email admin@byerfountainmotorinn.com.au and our team will be delighted to help.",
          callReception: true,
        };
        return c;
      });
    } finally { setBusy(false); }
  };

  const launchStay = () => {
    if (!dates.ci || !dates.co) return;
    openBooking({ checkIn: dates.ci, checkOut: dates.co, adults: dates.guests, children: 0 });
    setMsgs((m) => [...m, { role: "assistant", text: `Perfect — opening live availability for ${dates.ci} to ${dates.co}, ${dates.guests} guest(s). You'll always get our best rate booking direct. Safe travels!` }]);
    setPlan(false);
  };

  const openChat = () => { setOpen(true); setTeaser(false); };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <div className="fixed bottom-6 right-6 z-[70] flex items-end gap-3">
          <AnimatePresence>
            {teaser && (
              <motion.div data-testid="concierge-teaser"
                initial={{ opacity: 0, x: 20, scale: 0.9 }} animate={{ opacity: 1, x: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
                className="mb-1 max-w-[220px] relative glass soft-shadow rounded-2xl rounded-br-sm px-4 py-3 pr-7 border border-[#D4AF37]/30 hidden sm:block">
                <button onClick={openChat} className="text-left w-full">
                  <span className="text-[13px] text-[#0B192C] leading-snug">Hi! I'm <b>Aria</b> — need a room, a bite, or directions? Ask me anything.</span>
                </button>
                <button onClick={dismissTeaser} aria-label="Dismiss" data-testid="concierge-teaser-close"
                  className="absolute top-1.5 right-1.5 w-5 h-5 grid place-items-center rounded-full text-[#0B192C]/50 hover:bg-[#0B192C]/10 hover:text-[#0B192C] transition-colors">
                  <X size={12} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          <button onClick={openChat} data-testid="concierge-open" className="relative group">
            <span className="absolute inset-0 rounded-full bg-[#D4AF37] animate-ping opacity-30" />
            <span className="relative flex items-center gap-2 bg-[#0B192C] text-white pl-4 pr-5 py-3.5 rounded-full soft-shadow border border-[#D4AF37]/40 group-hover:bg-[#D4AF37] group-hover:text-[#0B192C] transition-colors duration-300">
              <Sparkles size={18} className="text-[#D4AF37] group-hover:text-[#0B192C]" />
              <span className="text-sm font-medium">Ask Aria</span>
            </span>
          </button>
        </div>
      )}

      {/* Window */}
      <AnimatePresence>
        {open && (
          <motion.div data-testid="concierge-window" data-lenis-prevent
            initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-6 right-6 z-[70] w-[92vw] max-w-[410px] h-[620px] max-h-[85vh] flex flex-col rounded-3xl overflow-hidden soft-shadow border border-[#D4AF37]/30 bg-[#F7F5F0]">
            {/* Header */}
            <div className="bg-[#0B192C] text-white px-5 py-4 flex items-center justify-between relative overflow-hidden">
              <div className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-[#D4AF37]/15 blur-2xl" />
              <div className="flex items-center gap-3 relative">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#b8932c] grid place-items-center text-[#0B192C]"><Sparkles size={18} /></div>
                <div>
                  <p className="font-serif text-xl leading-none">Aria</p>
                  <p className="text-[11px] text-white/60 mt-1 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" /> Concierge · Online now</p>
                </div>
              </div>
              <div className="flex items-center gap-2 relative">
                <LangDropdown lang={lang} setLang={setLang} langOpen={langOpen} setLangOpen={setLangOpen} />
                <button onClick={() => setOpen(false)} data-testid="concierge-close" className="text-white/70 hover:text-white"><X size={20} /></button>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={scroller}
              data-lenis-prevent
              className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-3"
              onWheel={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
            >
              {msgs.map((m, i) => (
                <div key={i}>
                  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div data-testid={`msg-${m.role}`}
                      className={`max-w-[84%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#0B192C] text-white rounded-br-sm" : "bg-white text-[#222] rounded-bl-sm soft-shadow"}`}>
                      {m.text ? cleanText(m.text) : <span className="inline-flex gap-1"><Dot/><Dot d={.15}/><Dot d={.3}/></span>}
                    </div>
                  </div>
                  {m.role === "assistant" && m.callReception && (
                    <div className="mt-2 flex justify-start" data-testid="reception-cta">
                      <a href={`tel:${RECEPTION_TEL}`} data-testid="call-reception-btn"
                        className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0B192C] text-sm font-medium rounded-full px-4 py-2 hover:bg-[#0B192C] hover:text-[#D4AF37] transition-colors">
                        <Phone size={14} /> Call Reception · {RECEPTION_DISPLAY}
                      </a>
                    </div>
                  )}
                  {m.role === "assistant" && m.callReception && receptionHours && (
                    <p className="text-[11px] text-[#0B192C]/50 mt-1 ml-1">Reception hours: {receptionHours}</p>
                  )}
                </div>
              ))}

              {/* Inline Plan-my-stay flow */}
              <AnimatePresence>
                {plan && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                    className="bg-white rounded-2xl p-4 soft-shadow border border-[#D4AF37]/30" data-testid="concierge-plan">
                    <p className="font-serif text-lg text-[#0B192C] mb-3">Let's plan your stay</p>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <label className="text-[10px] uppercase tracking-wide text-[#0B192C]/50">Check in
                        <input type="date" value={dates.ci} onChange={(e) => setDates({ ...dates, ci: e.target.value })} data-testid="plan-checkin"
                          className="mt-1 w-full bg-[#F7F5F0] border border-[#0B192C]/15 rounded-lg px-2 py-2 text-sm text-[#222]" />
                      </label>
                      <label className="text-[10px] uppercase tracking-wide text-[#0B192C]/50">Check out
                        <input type="date" value={dates.co} onChange={(e) => setDates({ ...dates, co: e.target.value })} data-testid="plan-checkout"
                          className="mt-1 w-full bg-[#F7F5F0] border border-[#0B192C]/15 rounded-lg px-2 py-2 text-sm text-[#222]" />
                      </label>
                    </div>
                    <label className="text-[10px] uppercase tracking-wide text-[#0B192C]/50 block mb-3">Guests
                      <input type="number" min={1} max={12} value={dates.guests} onChange={(e) => setDates({ ...dates, guests: +e.target.value })} data-testid="plan-guests"
                        className="mt-1 w-full bg-[#F7F5F0] border border-[#0B192C]/15 rounded-lg px-2 py-2 text-sm text-[#222]" />
                    </label>
                    <button onClick={launchStay} data-testid="plan-submit"
                      className="w-full bg-[#0B192C] text-white py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-[#D4AF37] hover:text-[#0B192C] transition-colors">
                      <CalendarCheck size={15} /> See live availability
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              {msgs.length <= 1 && !plan && (
                <div className="pt-1 space-y-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s.label} onClick={() => send(s.q)} data-testid="concierge-suggestion"
                      className="w-full flex items-center gap-3 text-left text-sm bg-white hover:bg-[#0B192C] hover:text-white text-[#0B192C] rounded-xl px-3.5 py-2.5 soft-shadow transition-colors group">
                      <s.icon size={16} className="text-[#D4AF37]" /> {s.label}
                      <ChevronRight size={15} className="ml-auto opacity-40 group-hover:translate-x-1 transition-transform" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Plan / Book bar */}
            <div className="px-3 pb-3">
              <div className="flex gap-2 mb-2">
                <button onClick={() => setPlan((p) => !p)} data-testid="concierge-plan-toggle"
                  className="flex-1 text-xs text-[#0B192C] bg-[#D4AF37]/25 hover:bg-[#D4AF37]/45 rounded-full py-2 transition-colors font-medium flex items-center justify-center gap-1.5">
                  <CalendarCheck size={14} /> Plan my stay
                </button>
                <a href={`tel:${RECEPTION_TEL}`} data-testid="concierge-call"
                  className="flex-1 text-xs text-white bg-[#0B192C] hover:bg-[#D4AF37] hover:text-[#0B192C] rounded-full py-2 transition-colors font-medium flex items-center justify-center gap-1.5">
                  <Phone size={13} /> Call Reception
                </a>
                <button onClick={() => { setOpen(false); openBookingModal(); }} data-testid="concierge-book"
                  className="flex-1 text-xs text-white bg-[#222] hover:bg-[#0B192C] rounded-full py-2 transition-colors font-medium">
                  Book direct
                </button>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-full pl-4 pr-2 py-1.5 border border-[#0B192C]/10">
                <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask Aria anything…" data-testid="concierge-input"
                  className="flex-1 bg-transparent outline-none text-sm text-[#222]" />
                <button onClick={() => send()} disabled={busy} data-testid="concierge-send"
                  className="w-9 h-9 grid place-items-center rounded-full bg-[#0B192C] text-white disabled:opacity-50 hover:bg-[#D4AF37] hover:text-[#0B192C] transition-colors">
                  <Send size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

const Dot = ({ d = 0 }) => (
  <span className="w-1.5 h-1.5 rounded-full bg-[#0B192C]/40 inline-block" style={{ animation: `bounce 1s ${d}s infinite` }} />
);

// Strip markdown emphasis characters that some models emit — the chat looks more
// human/professional to guests without the raw ** __ ` markers around words.
const cleanText = (s) => (s || "")
  .replace(/\*\*(.+?)\*\*/g, "$1")   // **bold** → bold
  .replace(/__(.+?)__/g, "$1")        // __bold__ → bold
  .replace(/(^|\s)\*(?!\s)(.+?)\*(?=\s|[.,!?;:]|$)/g, "$1$2")  // *italic* → italic
  .replace(/`([^`]+)`/g, "$1");        // `code` → code

// Language dropdown rendered via portal so it always escapes the Aria window's
// overflow-hidden container and never gets clipped by the header.
function LangDropdown({ lang, setLang, langOpen, setLangOpen }) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const current = (LANGS.find((L) => L.code === lang) || LANGS[0]);
  const label = current.label.split(" ")[0];

  useLayoutEffect(() => {
    if (!langOpen) return;
    const compute = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      setPos({ top: r.bottom + 8, right: Math.max(12, window.innerWidth - r.right) });
    };
    compute();
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute);
    return () => { window.removeEventListener("scroll", compute, true); window.removeEventListener("resize", compute); };
  }, [langOpen]);

  useEffect(() => {
    if (!langOpen) return;
    const onDoc = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setLangOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setLangOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [langOpen, setLangOpen]);

  return (
    <>
      <button ref={triggerRef} onClick={() => setLangOpen((v) => !v)} data-testid="concierge-lang-toggle"
        aria-haspopup="listbox" aria-expanded={langOpen}
        className="flex items-center gap-1.5 text-xs text-white bg-white/10 hover:bg-[#D4AF37] hover:text-[#0B192C] border border-white/25 rounded-full pl-3 pr-2.5 py-1.5 transition-colors">
        <Globe size={13} />
        <span className="font-medium">{label}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" className={`transition-transform ${langOpen ? "rotate-180" : ""}`}><path d="M1 3l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
      {langOpen && createPortal(
        <div ref={menuRef} role="listbox" aria-label="Select language"
          style={{ position: "fixed", top: pos.top, right: pos.right, width: 224, zIndex: 2147483000 }}
          className="max-h-72 overflow-y-auto overscroll-contain bg-white text-[#0B192C] rounded-xl shadow-2xl border border-[#0B192C]/10 py-1"
          data-testid="concierge-lang-menu" data-lenis-prevent
          onWheel={(e) => e.stopPropagation()}
        >
          {LANGS.map((L) => (
            <button key={L.code} onClick={() => { setLang(L.code); setLangOpen(false); }}
              role="option" aria-selected={lang === L.code}
              className={`w-full text-left px-3.5 py-2 text-sm hover:bg-[#D4AF37]/15 flex items-center justify-between gap-3 ${lang === L.code ? "bg-[#D4AF37]/20 font-medium" : ""}`}
              data-testid={`lang-${L.code}`}>
              <span>{L.label}</span>
              {lang === L.code && <span className="text-[#D4AF37] text-[13px]">✓</span>}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
